import React, { useState, useCallback, useEffect } from 'react';
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { connect } from 'react-redux';
import { State } from 'country-state-city';
import { format } from 'date-fns';

import ModernHeader from '../../../../components/layout/Headers/ModernHeader';
import { ROUTES } from '../../../../shared/constants/routes';
import { countries } from '../../../../shared/constants/ccBill';
import TaxImportantNoticeCard from './Card/TaxImportantNoticeCard';
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormControlWithFocus,
  FormMessage,
  useFormValidation,
} from "../../../../shared/components/form";
import { w8TaxInfoSchema } from '../../../../shared/validations/taxInfo';
import { showSuccess, showError } from '../../../../shared/utils/swalHandler';
import { useNavigation } from '../../../../shared/utils/useNavigation';
import { createW8Form, getW8Form } from '../../../../shared/actions/transactionAction';
import TaxBanditsSignatureRequiredCard from './Card/TaxBanditsSignatureRequiredCard';
import CompleteFormModal from '../../../Modals/EarningsPayout/CompleteFormModal';
import W8CertificationDeclaration from './Card/W8CertificationDeclaration';
import W8PrivacyNoticeCard from './Card/W8PrivacyNoticeCard';
import {
  shouldSuppressLoadError,
  DEFAULT_W8_FORM_VALUES,
  getW8FormResetValues,
} from '../../../../shared/constants/EarningsPayout';
import { getPendingSigningUrl } from '../../../../shared/utils/function';
import { encryptValue } from '../../../../shared/utils/config/encryption';
import './w8.css';

const SIGNATURE_DATE_DISPLAY_FORMAT = 'dd / MM / yyyy';

const TaxInformation = ({
  user
}) => {
  const isUsUser = false;
  const navigate = useNavigation();
  const [isLoading, setIsLoading] = useState(true);
  const [alreadyFilled, setAlreadyFilled] = useState(false);
  const [pendingSigningUrl, setPendingSigningUrl] = useState(null);
  const [showCompleteFormModal, setShowCompleteFormModal] = useState(false);

  const [permanentStateOptions, setPermanentStateOptions] = useState([]);
  const [mailingStateOptions, setMailingStateOptions] = useState([]);

  const form = useForm({
    resolver: zodResolver(w8TaxInfoSchema),
    defaultValues: DEFAULT_W8_FORM_VALUES,
    mode: "onChange",
  });

  const { validateAndFocus, shouldShowError } = useFormValidation(form, {
    showErrorsOnSubmit: true,
    focusOnFirstError: true,
  });

  const fieldInputClass = (name) =>
    `form-control cm_form_control ${shouldShowError(name) ? 'is-invalid' : ''}`;

  const countryOfCitizenship = form.watch('countryOfCitizenship');
  const permanentCountry = form.watch('permanentCountry');
  const mailingCountry = form.watch('mailingCountry');
  const ftinNotRequired = form.watch('ftinNotRequired');
  const countryOfResidenceForTaxTreaty = form.watch('countryOfResidenceForTaxTreaty');

  // Load state/province options for permanent address
  useEffect(() => {
    if (!permanentCountry) {
      setPermanentStateOptions([]);
      return;
    }

    const normalizedCountry = permanentCountry.toUpperCase();
    const states = State.getStatesOfCountry(normalizedCountry) || [];
    const options = states.map((state) => ({
      code: state.isoCode,
      name: state.name,
    }));

    setPermanentStateOptions(options);
  }, [permanentCountry]);

  // Fetch W-9 form data on component mount
  useEffect(() => {
    let isActive = true;

    const fetchW8FormData = async () => {
      setIsLoading(true);
      try {
        const response = await getW8Form();
        if (!isActive) return;

        if (!response?.success) {
          setAlreadyFilled(false);
          setPendingSigningUrl(null);
          setShowCompleteFormModal(false);
          form.reset(DEFAULT_W8_FORM_VALUES);
          form.setValue('signatureDate', format(new Date(), SIGNATURE_DATE_DISPLAY_FORMAT));
          if (response?.error) {
            // Non-blocking: user can still fill the form.
            if (!shouldSuppressLoadError(response.error)) {
              showError(
                'Unable to load saved W-8',
                `${response.error} You can still fill and submit the form below.`
              );
            }
          }
          return;
        }

        const formData = response?.data?.data;
        const hasFormData =
          formData && typeof formData === 'object' && Object.keys(formData).length > 0;

        setAlreadyFilled(Boolean(hasFormData));
        setPendingSigningUrl(hasFormData ? getPendingSigningUrl(formData?.taxBanditsResponse) : null);
        setShowCompleteFormModal(false);
        form.reset(getW8FormResetValues(hasFormData, formData, DEFAULT_W8_FORM_VALUES));
        if (!hasFormData) {
          form.setValue('signatureDate', format(new Date(), SIGNATURE_DATE_DISPLAY_FORMAT));
        }
      } catch (error) {
        if (!isActive) return;
        setAlreadyFilled(false);
        setPendingSigningUrl(null);
        setShowCompleteFormModal(false);
        form.reset(DEFAULT_W8_FORM_VALUES);
        form.setValue('signatureDate', format(new Date(), SIGNATURE_DATE_DISPLAY_FORMAT));
        showError('Error', error?.message || 'Failed to load W-8 form. Please try again.');
      } finally {
        if (isActive) setIsLoading(false);
      }
    };

    fetchW8FormData();
    return () => {
      isActive = false;
    };
  }, [form]);


  // Load state/province options for mailing address
  useEffect(() => {
    if (!mailingCountry) {
      setMailingStateOptions([]);
      return;
    }

    const normalizedCountry = mailingCountry.toUpperCase();
    const states = State.getStatesOfCountry(normalizedCountry) || [];
    const options = states.map((state) => ({
      code: state.isoCode,
      name: state.name,
    }));

    setMailingStateOptions(options);
  }, [mailingCountry]);

  const onSubmit = useCallback(async (data) => {
    try {
      // Encrypt foreignTaxId and usTaxpayerId before sending to backend
      if (data.foreignTaxId) {
        data.foreignTaxId = await encryptValue(data.foreignTaxId);
      }
      if (data.usTaxpayerId) {
        data.usTaxpayerId = await encryptValue(data.usTaxpayerId);
      }
      const response = await createW8Form(data);
      if (response?.success) {
        const signingUrl = getPendingSigningUrl(response?.data?.taxBanditsResponse);
        if (signingUrl) {
          setPendingSigningUrl(signingUrl);
          setShowCompleteFormModal(true);
        } else {
          await showSuccess('Success', 'W-8 form submitted successfully.');
          navigate.toEarningsPayout();
        }
      } else {
        await showError('Error', response?.message || response?.error || 'Failed to submit W-8 form. Please try again.');
      }
    } catch (error) {
      const errorMessage = error?.message || 'An unexpected error occurred. Please try again.';
      await showError('Error', errorMessage);
    }
  }, [navigate]);

  const handleFormSubmit = useCallback(
    async (data) => {
      const signatureDate = format(new Date(), SIGNATURE_DATE_DISPLAY_FORMAT);
      form.setValue('signatureDate', signatureDate);
      const isValid = await validateAndFocus();
      if (!isValid) return;
      await onSubmit({ ...data, signatureDate });
    },
    [form, onSubmit, validateAndFocus]
  );

  return (
    <div className="modern-earnings-payout-page">
      <ModernHeader
        customTitle="W-8 Tax Form"
        showButtonBorder={false}
        hideSearchIcon
        showAvatar={false}
        showBackButton
        backButtonUrl={ROUTES.EARNINGS_PAYOUT}
      />

      {/* Important Notice Card */}
      <TaxImportantNoticeCard
        isUsUser={isUsUser}
      />

      {/* Signature Required Card */}
      <TaxBanditsSignatureRequiredCard
        pendingSigningUrl={pendingSigningUrl}
        isUsUser={isUsUser}
      />

      <CompleteFormModal
        isOpen={showCompleteFormModal}
        signingUrl={pendingSigningUrl}
        isUsUser={isUsUser}
      />

      <Form {...form}>
        <form
          className="cm_dark_form"
          onSubmit={form.handleSubmit(handleFormSubmit)}
        >
          <div className='w8_card'>
            {/* Identification of Beneficial Owner Section */}

            <h2>Identification of Beneficial Owner</h2>

            <div className="form-group">
              <FormLabel>
                Name of Individual (line 1) <span className="w8-required-asterisk">*</span>
              </FormLabel>
              <div className="d-flex gap-2">
                <FormField
                  control={form.control}
                  name="firstName"
                  render={({ field }) => (
                    <FormItem className="w-full">
                      <FormControlWithFocus fieldName="firstName">
                        <input
                          type="text"
                          className={fieldInputClass('firstName')}
                          placeholder="First name"
                          disabled={alreadyFilled}
                          {...field}
                        />
                      </FormControlWithFocus>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="lastName"
                  render={({ field }) => (
                    <FormItem className="w-full">
                      <FormControlWithFocus fieldName="lastName">
                        <input
                          type="text"
                          className={fieldInputClass('lastName')}
                          placeholder="Last name"
                          disabled={alreadyFilled}
                          {...field}
                        />
                      </FormControlWithFocus>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            <FormField
              control={form.control}
              name="countryOfCitizenship"
              render={({ field }) => (
                <FormItem className="form-group">
                  <FormLabel>Country of Citizenship <span className="w8-required-asterisk">*</span></FormLabel>
                  <FormControl>
                    <select
                      name={field.name}
                      className={fieldInputClass('countryOfCitizenship')}
                      value={field.value && field.value !== 'OTHER' ? field.value : ''}
                      onChange={(e) => {
                        if (e.target.value) {
                          field.onChange(e.target.value);
                          form.setValue('otherCountryOfCitizenship', '');
                        } else {
                          field.onChange('');
                        }
                      }}
                      onBlur={field.onBlur}
                      ref={field.ref}
                      disabled={alreadyFilled}
                    >
                      <option value="">Select country</option>
                      {countries.map((country) => (
                        <option key={country.code} value={country.code}>
                          {country.name}
                        </option>
                      ))}
                    </select>
                  </FormControl>
                  <FormMessage />

                  <div className="mt-3 px-4">
                    <label className="d-flex align-items-center gap-3 w8-label-clickable">
                      <input
                        type="radio"
                        name={field.name}
                        checked={field.value === 'OTHER'}
                        onChange={(e) => {
                          if (e.target.checked) {
                            field.onChange('OTHER');
                          }
                        }}
                        onBlur={field.onBlur}
                        className={`form-check-input ${alreadyFilled && field.value === 'OTHER' ? 'taxinfo-radio-disabled-checked' : ''}`}
                        disabled={alreadyFilled}
                      />
                      <span className='pl-2'>Other country (specify below)</span>
                    </label>
                  </div>

                  {countryOfCitizenship === 'OTHER' && (
                    <div className="mt-3">
                      <FormField
                        control={form.control}
                        name="otherCountryOfCitizenship"
                        render={({ field: otherField }) => (
                          <FormItem>
                            <FormControl>
                              <input
                                type="text"
                                className={fieldInputClass('otherCountryOfCitizenship')}
                                placeholder="Enter country name"
                                disabled={alreadyFilled}
                                {...otherField}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  )}
                </FormItem>
              )}
            />

            <h3 className='mb-2'>
              Permanent Residence Address <span className="w8-required-asterisk">*</span>
            </h3>

            <FormField
              control={form.control}
              name="permanentStreetAddress"
              render={({ field }) => (
                <FormItem className="form-group">
                  <FormControlWithFocus fieldName="permanentStreetAddress">
                    <input
                      type="text"
                      className={fieldInputClass('permanentStreetAddress')}
                      placeholder="Street address"
                      disabled={alreadyFilled}
                      {...field}
                    />
                  </FormControlWithFocus>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="form-group">
              <div className="d-flex gap-2">
                <FormField
                  control={form.control}
                  name="permanentCity"
                  render={({ field }) => (
                    <FormItem className="w-full">
                      <FormControlWithFocus fieldName="permanentCity">
                        <input
                          type="text"
                          className={fieldInputClass('permanentCity')}
                          placeholder="City"
                          disabled={alreadyFilled}
                          {...field}
                        />
                      </FormControlWithFocus>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="permanentStateProvince"
                  render={({ field }) => (
                    <FormItem className="w-full">
                      <FormControlWithFocus fieldName="permanentStateProvince">
                        {permanentStateOptions.length > 0 ? (
                          <select
                            name={field.name}
                            className={fieldInputClass('permanentStateProvince')}
                            value={field.value}
                            onChange={(e) => field.onChange(e.target.value)}
                            onBlur={field.onBlur}
                            ref={field.ref}
                            disabled={alreadyFilled}
                          >
                            <option value="">Select State/Province</option>
                            {permanentStateOptions.map((state) => (
                              <option key={state.code} value={state.code}>
                                {state.name}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <input
                            type="text"
                            className={fieldInputClass('permanentStateProvince')}
                            placeholder="State/Province"
                            disabled={alreadyFilled}
                            {...field}
                          />
                        )}
                      </FormControlWithFocus>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            <div className="form-group">
              <div className="d-flex gap-2">
                <FormField
                  control={form.control}
                  name="permanentPostalCode"
                  render={({ field }) => (
                    <FormItem className="w-full">
                      <FormControlWithFocus fieldName="permanentPostalCode">
                        <input
                          type="text"
                          className={fieldInputClass('permanentPostalCode')}
                          placeholder="ZIP/Postal Code"
                          maxLength={16}
                          disabled={alreadyFilled}
                          {...field}
                        />
                      </FormControlWithFocus>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="permanentCountry"
                  render={({ field }) => (
                    <FormItem className="w-full">
                      <FormControlWithFocus fieldName="permanentCountry">
                        <select
                          name={field.name}
                          className={fieldInputClass('permanentCountry')}
                          value={field.value}
                          onChange={(e) => {
                            field.onChange(e.target.value);
                            form.setValue('permanentStateProvince', '');
                          }}
                          onBlur={field.onBlur}
                          ref={field.ref}
                          disabled={alreadyFilled}
                        >
                          <option value="">Select Country</option>
                          {countries.map((country) => (
                            <option key={country.code} value={country.code}>
                              {country.name}
                            </option>
                          ))}
                        </select>
                      </FormControlWithFocus>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            <h3 className='mb-3'>
              Mailing Address (if different)
            </h3>

            <FormField
              control={form.control}
              name="mailingStreetAddress"
              render={({ field }) => (
                <FormItem className="form-group">
                  <FormControlWithFocus fieldName="mailingStreetAddress">
                    <input
                      type="text"
                      className={fieldInputClass('mailingStreetAddress')}
                      placeholder="Street address"
                      disabled={alreadyFilled}
                      {...field}
                    />
                  </FormControlWithFocus>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="form-group">
              <div className="d-flex gap-2">
                <FormField
                  control={form.control}
                  name="mailingCity"
                  render={({ field }) => (
                    <FormItem className="w-full">
                      <FormControlWithFocus fieldName="mailingCity">
                        <input
                          type="text"
                          className={fieldInputClass('mailingCity')}
                          placeholder="City"
                          disabled={alreadyFilled}
                          {...field}
                        />
                      </FormControlWithFocus>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="mailingStateProvince"
                  render={({ field }) => (
                    <FormItem className="w-full">
                      <FormControlWithFocus fieldName="mailingStateProvince">
                        {mailingStateOptions.length > 0 ? (
                          <select
                            name={field.name}
                            className={fieldInputClass('mailingStateProvince')}
                            value={field.value}
                            onChange={(e) => field.onChange(e.target.value)}
                            onBlur={field.onBlur}
                            ref={field.ref}
                            disabled={alreadyFilled}
                          >
                            <option value="">Select State/Province</option>
                            {mailingStateOptions.map((state) => (
                              <option key={state.code} value={state.code}>
                                {state.name}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <input
                            type="text"
                            className={fieldInputClass('mailingStateProvince')}
                            placeholder="State/Province"
                            disabled={alreadyFilled}
                            {...field}
                          />
                        )}
                      </FormControlWithFocus>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            <div className="form-group">
              <div className="d-flex gap-2">
                <FormField
                  control={form.control}
                  name="mailingPostalCode"
                  render={({ field }) => (
                    <FormItem className="w-full">
                      <FormControlWithFocus fieldName="mailingPostalCode">
                        <input
                          type="text"
                          className={fieldInputClass('mailingPostalCode')}
                          placeholder="ZIP/Postal Code"
                          maxLength={16}
                          disabled={alreadyFilled}
                          {...field}
                        />
                      </FormControlWithFocus>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="mailingCountry"
                  render={({ field }) => (
                    <FormItem className="w-full with_down_arrow">
                      <FormControlWithFocus fieldName="mailingCountry">
                        <select
                          name={field.name}
                          className={fieldInputClass('mailingCountry')}
                          value={field.value}
                          onChange={(e) => {
                            field.onChange(e.target.value);
                            form.setValue('mailingStateProvince', '');
                          }}
                          onBlur={field.onBlur}
                          ref={field.ref}
                          disabled={alreadyFilled}
                        >
                          <option value="">Select Country</option>
                          {countries.map((country) => (
                            <option key={country.code} value={country.code}>
                              {country.name}
                            </option>
                          ))}
                        </select>

                      </FormControlWithFocus>

                      <FormMessage />
                      <i className="fa-solid fa-chevron-down"></i>
                    </FormItem>
                  )}
                />
              </div>
            </div>
          </div>

          <div className='w8_card'>
            <h2>Tax Identification</h2>

            <FormField
              control={form.control}
              name="usTaxpayerId"
              render={({ field }) => (
                <FormItem className="form-group">
                  <FormLabel>U.S. Taxpayer Identification Number (if any)</FormLabel>
                  <FormControlWithFocus fieldName="usTaxpayerId">
                    <input
                      type="text"
                      className={fieldInputClass('usTaxpayerId')}
                      placeholder="SSN or ITIN"
                      disabled={alreadyFilled}
                      {...field}
                    />
                  </FormControlWithFocus>
                  <small className="text-muted d-block mt-1 w8-helper-text">
                    Social Security Number or Individual Taxpayer Identification Number
                  </small>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="foreignTaxId"
              render={({ field }) => (
                <FormItem className="form-group">
                  <FormLabel>
                    Foreign Tax Identifying Number
                    {!ftinNotRequired && <span className="w8-required-asterisk"> *</span>}
                  </FormLabel>
                  <FormControlWithFocus fieldName="foreignTaxId">
                    <input
                      type="text"
                      className={fieldInputClass('foreignTaxId')}
                      placeholder="Enter foreign TIN"
                      {...field}
                      disabled={ftinNotRequired || alreadyFilled}
                    />
                  </FormControlWithFocus>
                  <small className="text-muted d-block mt-1 w8-helper-text">
                    Tax ID from your country of residence
                  </small>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="ftinNotRequired"
              render={({ field }) => (
                <FormItem className="form-group px-4">
                  <FormControl>
                    <label className="d-flex align-items-center gap-2 w8-label-clickable">
                      <input
                        type="checkbox"
                        name={field.name}
                        className="form-check-input"
                        checked={field.value}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          field.onChange(checked);
                          if (checked) {
                            form.setValue('foreignTaxId', '');
                            form.clearErrors('foreignTaxId');
                            // Re-run validation so schema sees ftinNotRequired; defer so state is applied
                            setTimeout(() => form.trigger(['foreignTaxId', 'ftinNotRequired']), 0);
                          } else {
                            form.trigger('foreignTaxId');
                          }
                        }}
                        onBlur={field.onBlur}
                        ref={field.ref}
                        disabled={alreadyFilled}
                      />
                      <span className='pl-2'>Check if FTIN not legally required</span>
                    </label>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="referenceNumber"
              render={({ field }) => (
                <FormItem className="form-group">
                  <FormLabel>Reference Number(s)</FormLabel>
                  <FormControlWithFocus fieldName="referenceNumber">
                    <input
                      type="text"
                      className={fieldInputClass('referenceNumber')}
                      placeholder="Optional reference number"
                      disabled={alreadyFilled}
                      {...field}
                    />
                  </FormControlWithFocus>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className='w8_card'>
            <h2>Claim of Tax Treaty Benefits</h2>

            <FormField
              control={form.control}
              name="countryOfResidenceForTaxTreaty"
              render={({ field }) => (
                <FormItem className="form-group">
                  <FormLabel>Country of Residence for Tax Treaty <span className="w8-required-asterisk">*</span></FormLabel>
                  <FormControl>
                    <select
                      name={field.name}
                      className={fieldInputClass('countryOfResidenceForTaxTreaty')}
                      value={field.value && field.value !== 'OTHER' ? field.value : ''}
                      onChange={(e) => {
                        if (e.target.value) {
                          field.onChange(e.target.value);
                          form.setValue('otherCountryOfResidenceForTaxTreaty', '');
                        } else {
                          field.onChange('');
                        }
                      }}
                      onBlur={field.onBlur}
                      ref={field.ref}
                      disabled={alreadyFilled}
                    >
                      <option value="">Select country</option>
                      {countries.map((country) => (
                        <option key={country.code} value={country.code}>
                          {country.name}
                        </option>
                      ))}
                    </select>
                  </FormControl>
                  <FormMessage />

                  <div className="mt-3 px-4">
                    <label className="d-flex align-items-center gap-3 w8-label-clickable">
                      <input
                        type="radio"
                        name={field.name}
                        checked={field.value === 'OTHER'}
                        onChange={(e) => {
                          if (e.target.checked) {
                            field.onChange('OTHER');
                          }
                        }}
                        onBlur={field.onBlur}
                        className={`form-check-input ${alreadyFilled && field.value === 'OTHER' ? 'taxinfo-radio-disabled-checked' : ''}`}
                        disabled={alreadyFilled}
                      />
                      <span className='pl-2'>Other country (specify below)</span>
                    </label>


                  </div>
                  {countryOfResidenceForTaxTreaty === 'OTHER' && (
                    <div className="mt-3">
                      <FormField
                        control={form.control}
                        name="otherCountryOfResidenceForTaxTreaty"
                        render={({ field: otherField }) => (
                          <FormItem>
                            <FormControl>
                              <input
                                type="text"
                                className={fieldInputClass('otherCountryOfResidenceForTaxTreaty')}
                                placeholder="Enter country name"
                                disabled={alreadyFilled}
                                {...otherField}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  )}
                </FormItem>
              )}
            />

            <div className="d-flex gap-3 mb-4 important_notice_card">
              <div>
                <i className="fa-solid fa-circle-info"></i>
              </div>
              <div className="w-full">
                <p>
                  Claiming treaty benefits requires you to specify the article and paragraph of the treaty under which you qualify.
                </p>
              </div>
            </div>

            <FormField
              control={form.control}
              name="articleAndParagraph"
              render={({ field }) => (
                <FormItem className="form-group">
                  <FormLabel>Article and Paragraph</FormLabel>
                  <FormControlWithFocus fieldName="articleAndParagraph">
                    <input
                      type="text"
                      className={fieldInputClass('articleAndParagraph')}
                      placeholder="e.g., Article 10, Paragraph 2"
                      disabled={alreadyFilled}
                      {...field}
                    />
                  </FormControlWithFocus>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="rateOfWithholding"
              render={({ field }) => (
                <FormItem className="form-group">
                  <FormLabel>Rate of Withholding (%)</FormLabel>
                  <FormControlWithFocus fieldName="rateOfWithholding">
                    <input
                      type="number"
                      name={field.name}
                      min="0"
                      max="100"
                      step="0.01"
                      className={fieldInputClass('rateOfWithholding')}
                      placeholder="0"
                      value={field.value || 0}
                      onChange={(e) => {
                        const value = e.target.value === '' ? 0 : parseFloat(e.target.value) || 0;
                        field.onChange(value);
                      }}
                      onBlur={field.onBlur}
                      ref={field.ref}
                      disabled={alreadyFilled}
                    />
                  </FormControlWithFocus>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="typeOfIncome"
              render={({ field }) => (
                <FormItem className="form-group">
                  <FormLabel>Type of Income</FormLabel>
                  <FormControl>
                    <select
                      name={field.name}
                      className={fieldInputClass('typeOfIncome')}
                      value={field.value || ''}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      ref={field.ref}
                      disabled={alreadyFilled}
                    >
                      <option value="">Select income type</option>
                      <option value="dividends">Dividends</option>
                      <option value="interest">Interest</option>
                      <option value="royalties">Royalties</option>
                      <option value="pensions">Pensions</option>
                      <option value="annuities">Annuities</option>
                      <option value="other">Other</option>
                    </select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="additionalConditionsForTreatyBenefits"
              render={({ field }) => (
                <FormItem className="form-group">
                  <FormLabel>Additional Conditions for Treaty Benefits</FormLabel>
                  <FormControlWithFocus fieldName="additionalConditionsForTreatyBenefits">
                    <textarea
                      className={fieldInputClass('additionalConditionsForTreatyBenefits')}
                      placeholder="Explain why the beneficial owner qualifies for the treaty's reduced withholding rate."
                      rows="4"
                      disabled={alreadyFilled}
                      {...field}
                    />
                  </FormControlWithFocus>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className='w8_card'>
            <h2 className='mb-0'>Certification</h2>
            <W8CertificationDeclaration />

            <FormField
              control={form.control}
              name="certification"
              render={({ field }) => (
                <FormItem className="form-group px-4">
                  <FormControl>
                    <label className="d-flex w8-label-clickable">
                      <input
                        type="checkbox"
                        name={field.name}
                        className="form-check-input"
                        checked={field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                        onBlur={field.onBlur}
                        ref={field.ref}
                        disabled={alreadyFilled}
                      />
                      <span className='pl-2'>
                        I certify that I have the capacity to sign for the person identified on line 1 of this form.
                      </span>
                    </label>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="digitalSignature"
              render={({ field }) => (
                <FormItem className="form-group">
                  <FormLabel>Digital Signature <span className="w8-required-asterisk">*</span></FormLabel>
                  <FormControlWithFocus fieldName="digitalSignature">
                    <input
                      type="text"
                      className={fieldInputClass('digitalSignature')}
                      placeholder="Type your full name"
                      disabled={alreadyFilled}
                      {...field}
                    />
                  </FormControlWithFocus>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="signatureDate"
              render={({ field }) => (
                <FormItem className="form-group">
                  <FormLabel>Date <span className="w8-required-asterisk">*</span></FormLabel>
                  <FormControlWithFocus fieldName="signatureDate">
                    <input
                      ref={field.ref}
                      type="text"
                      className={`${fieldInputClass('signatureDate')} w8-input-readonly`}
                      value={field.value || ''}
                      readOnly
                      aria-readonly="true"
                    />
                  </FormControlWithFocus>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="capacityInWhichActing"
              render={({ field }) => (
                <FormItem className="form-group">
                  <FormLabel>Capacity in Which Acting</FormLabel>
                  <FormControlWithFocus fieldName="capacityInWhichActing">
                    <input
                      type="text"
                      className={fieldInputClass('capacityInWhichActing')}
                      placeholder="e.g., Individual, Guardian"
                      disabled={alreadyFilled}
                      {...field}
                    />
                  </FormControlWithFocus>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          {/* Secure Submission Notice */}
          <W8PrivacyNoticeCard />

          {!alreadyFilled && (
            <button
              type="submit"
              className="btn btn_primary rounded_8 w-full"
              disabled={form.formState.isSubmitting || alreadyFilled}
            >
              {form.formState.isSubmitting ? 'Submitting...' : 'Submit W-8BEN Form'}
            </button>
          )}
        </form>
      </Form>
    </div>
  );
};

const mapStateToProps = (state) => ({
  user: state.user?.user
});

export default connect(mapStateToProps)(TaxInformation);